import { useState, type FormEvent } from 'react'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { CampoDeTextoLongo } from '../../../components/campos'
import { AvisoDeErro } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import type { AcaoDisponivel } from '../../../types/gmud'

interface Props {
  acao: AcaoDisponivel
  /** Só muda a cor do botão de confirmar. Quem classifica é `acoesDestrutivas.ts`. */
  destrutiva?: boolean
  enviando: boolean
  erro: unknown
  aoConfirmar: (motivo: string) => void
  aoCancelar: () => void
}

/**
 * Pede a justificativa antes de executar a ação.
 *
 * Quem exige o motivo é o servidor (`exigeMotivo` vem de `/transicoes-possiveis`, que lê
 * `ref_transicao_permitida`) — a tela não tem uma lista própria de "ações que precisam de
 * justificativa". A verificação de campo vazio daqui é cortesia: evita uma ida ao servidor
 * para receber de volta o óbvio. Quem decide continua sendo o backend.
 *
 * Escrever a justificativa já é a pausa que uma ação destrutiva exige: por isso quem exige
 * motivo não passa antes por um segundo diálogo de "tem certeza?" — nesse caso a única
 * diferença é a cor do botão de confirmar.
 *
 * @param props a ação escolhida, o estado do envio e os desfechos
 * @returns o diálogo de justificativa
 */
export function DialogoDeMotivo({ acao, destrutiva = false, enviando, erro, aoConfirmar, aoCancelar }: Props) {
  const [motivo, setMotivo] = useState('')
  const [faltou, setFaltou] = useState(false)

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!motivo.trim()) {
      setFaltou(true)
      return
    }
    aoConfirmar(motivo.trim())
  }

  return (
    <Dialogo
      titulo={acao.rotulo}
      descricao={`A solicitação passa para "${acao.statusDestinoRotulo}". A justificativa fica registrada com o seu nome e não pode ser apagada depois.`}
      aoFechar={aoCancelar}
      rodape={
        <>
          <Botao onClick={aoCancelar} disabled={enviando}>
            Cancelar
          </Botao>
          <Botao aparencia={destrutiva ? 'perigo' : 'primario'} onClick={enviar} disabled={enviando}>
            {enviando ? 'Enviando…' : `Confirmar: ${acao.rotulo}`}
          </Botao>
        </>
      }
    >
      <form onSubmit={enviar} className="space-y-3">
        <CampoDeTextoLongo
          rotulo="Justificativa"
          ajuda="Escreva o que motivou a decisão. Este texto vai para a trilha de auditoria e para o histórico da solicitação."
          erro={faltou ? TEXTOS.motivoObrigatorio : undefined}
          value={motivo}
          rows={5}
          onChange={(evento) => {
            setMotivo(evento.target.value)
            setFaltou(false)
          }}
        />
        {Boolean(erro) && <AvisoDeErro erro={erro} titulo="A ação não foi concluída" />}
      </form>
    </Dialogo>
  )
}
